import ModifyLayout from '../../ModifyLayout';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { RecordFilter } from '@/types/api';
import styled from '@emotion/styled';
import ContentHeader, {
  ContentHeaderProps,
  DocViewMode,
} from '@/components/Record/TalkFLow/Contents/ContentHeader';
import BoardAndRecord from './BoardAndRecord';
import RecordUpload from '@/components/Record/RecordUpload';
import DetailContent from '@/components/Record/SubjectContent/DetailContent';
import FileItem from '@/components/Record/SubjectContent/AttachmentFile/FileContent/FileItem';
import AutoAlert from '@/components/Record/TalkFLow/Contents/AutoAlert';
import AiSummary from '@/components/Record/TalkFLow/Contents/AiSummary';
import { Box } from '@mui/material';
import VoiceRecord2 from '@/components/Record/VoiceRecord2';
import { useIntersectionObserver } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { GTMEventName } from '@/constant/gtm';
import {
  getPrevPageArea,
  useGTMEventTracker,
} from '@/hooks/gtm/useGTMEventTracker';
import { getDocumentDetailParam } from '@/utils/gtm/work';
import LexicalBoardEditor, {
  EditorRef,
} from '@/components/LexicalEditor/BoardEditor';
import { EditorState, LexicalEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import usePlatform from '@/components/Record/hooks/usePlatform';
import { isEmptyContent } from '@/utils/editor';
import useLayoutSize from '@/hooks/useLayoutSize';
import { LayoutType } from '@/constant/layout';
import classNames from 'classnames';
import UploadingFile from '@/components/Record/TalkFLow/Contents/UploadingFile';
import UploadFailedNotice from '@/components/Record/TalkFLow/Contents/UploadFailedNotice';
import { useAtomValue } from 'jotai';
import { uploadingState } from '@/recoil/atom';
import { useDialog } from '@/hooks/global-tools';
import { useTalkFlowDocument } from '@/hooks/apiHooks';
import { ensureLexicalHtml } from '@/components/Document/@hooks/useAiSummaries';
import OrganizingSkeleton from '@/components/Record/TalkFLow/Contents/OrganizingSkeleton';
import OrganizeError from '@/components/Record/TalkFLow/Contents/OrganizeError';
import useRecordTranslationView from '@/components/Record/TalkFLow/hooks/useRecordTranslationView';

type Props = {
  docId: string;
  topOffset: number;
  isOwner: boolean;
  searchText?: string;
  isWordReplacePanelOpen?: boolean;
  focusedResultId?: string;
  hoveredResultId?: string | null;
  scrollTrigger?: number;
  onCloseWordReplacePanel?: () => void;
  /** 본문 에디터 툴바 '재정리 요청' 버튼 — 수동 재정리 트리거 (PORTAL-12850) */
  onReorganize?: () => void;
  // AI 정리(organize) 실패 여부 + 재시도 (AI 요약과 분리된 본문 전용 에러 배너)
  organizeError?: boolean;
  onRetryOrganize?: () => void;
  // 앱(WebView)의 OPEN_AI 메시지 수신 시 실행. 예전에는 AI Chat 을 열었으나,
  // 지금은 녹음 업로드 시와 동일한 자동정리(AI 정리) 로직을 태운다.
  onRequestAutoSummarize?: () => void;
} & Pick<ContentHeaderProps, 'aiButtonProps' | 'wordReplaceButtonProps'>;

export type ContentsRef = {
  handleAiOnCreate: (value: string) => void;
  // AI 채팅 답변을 종합정리 본문(content)에 반영 (작성자 전용)
  appendToContent: (text: string) => Promise<void>;
  overwriteContent: (text: string) => Promise<void>;
};

const Contents = forwardRef<ContentsRef, Props>(
  (
    {
      docId,
      topOffset,
      aiButtonProps,
      wordReplaceButtonProps,
      isOwner,
      searchText = '',
      isWordReplacePanelOpen = false,
      focusedResultId,
      hoveredResultId,
      scrollTrigger,
      onCloseWordReplacePanel,
      onReorganize,
      organizeError = false,
      onRetryOrganize,
      onRequestAutoSummarize,
    }: Props,
    ref,
  ) => {
    const eventTracker = useGTMEventTracker();
    const { isMobile } = useLayoutSize(LayoutType.WORK_MODAL);
    const { isMobilePath } = usePlatform();
    const { watch, getValues, setValue } = useFormContext<RecordFilter>();
    const dialog = useDialog();
    const { t } = useTranslation();
    const contentRef = useRef<HTMLDivElement>(null);
    const entry = useIntersectionObserver(
      contentRef as React.RefObject<HTMLElement>,
      {},
    );
    const aiButtonPropsRef = useRef(aiButtonProps);
    // OPEN_AI 메시지 핸들러가 최신 콜백을 참조하도록 ref 로 보관(stale closure 방지)
    const onRequestAutoSummarizeRef = useRef(onRequestAutoSummarize);

    const editorRef = useRef<EditorRef>(null);
    const memoEditorRef = useRef<EditorRef>(null);
    const editorCoverRef = useRef<HTMLDivElement>(null);
    const [openAutoAlert, setOpenAutoAlert] = useState(false);
    const [isHover, setIsHover] = useState(false);
    const [prevContent, setPrevContent] = useState<string | null>(null);
    const [prevMemo, setPrevMemo] = useState<string | null>(null);
    // 본문 좌측 상단 "AI 정리(content) / 메모(memo)" 토글 상태
    const [viewMode, setViewMode] = useState<DocViewMode>('content');
    const content = watch('content');
    // 메모는 form 필드 'memo' 에 바인딩 → 자동저장(modifyTalkFlow)에 함께 전송됨
    const memo = watch('memo');
    const records = watch('recordings');

    // AI 정리 생성 중 여부 (스켈레톤/탭전환 트리거)
    const isGenerating = Boolean(aiButtonProps.isGenerating);

    // 정리가 한 번이라도 생성된 이력 = AI 요약(commonSummary)이 서버에 존재.
    // 로컬 state 대신 문서 응답에서 파생하므로 모달을 닫았다 다시 열어도 유지된다.
    // (AiSummary 와 동일한 useTalkFlowDocument → SWR 캐시 공유, 추가 요청 없음)
    const { data: talkFlowDoc } = useTalkFlowDocument(docId);

    const { hasCurrentResult: isTranslationActive, pick } =
      useRecordTranslationView(docId);
    // 번역 표시 중에는 작성자에게도 읽기 뷰를 보여준다.
    // 에디터에 번역본을 넣으면 1초 디바운스 자동저장(modifyTalkFlow)이 원문을 덮어쓰고,
    // 원문을 넣으면 화면의 다른 번역 영역과 어긋난다. 취소하면 편집 모드로 돌아온다.
    const showEditor = isOwner && !isTranslationActive;
    // 단어 바꾸기는 원문을 수정하는 기능이라 번역 표시 중에는 진입을 막는다.
    // 오버레이도 pick 을 거치지 않고 원문을 그려 화면이 서로 어긋난다.
    const wordReplaceDisabled = isTranslationActive;
    const hasGeneratedSummary = Boolean(
      talkFlowDoc?.commonSummary?.text?.trim(),
    );

    // AI 정리(content)가 비어있으면 작성자는 AI 정리 탭 비활성 → 메모만 작성.
    // 단, 정리 생성 중(isGenerating) 또는 정리 실패(organizeError)에는 AI 정리 탭에서
    // 스켈레톤/에러 배너를 보여줘야 하므로 비활성 해제한다.
    // 또한 정리 생성 이력(AI 요약 존재)이 있으면(편집 중 비운 경우) 비활성/강제전환하지 않는다.
    const isContentEmpty = isEmptyContent(content);
    const contentTabDisabled =
      Boolean(isOwner) &&
      isContentEmpty &&
      !isGenerating &&
      !organizeError &&
      !hasGeneratedSummary;

    // 메모 탭 노출: 작성자는 편집용, 비작성자(공유 문서)는 읽기 전용 조회용으로
    // 항상 노출한다. (QA PORTAL-12512: owner 가 아니어도 메모 탭을 볼 수 있어야 함)
    const showMemoTab = true;

    // 재정리 실행 가능 여부 — 정리할 글(메모)도 없고 완료된 녹음도 없으면 비활성.
    // 별도 안내문구 없이 버튼 disabled 로만 표현한다 (PORTAL-12850).
    const hasCompletedRecording = records.some((r) => r.state === 'complete');
    const canReorganize =
      hasCompletedRecording || Boolean(memo && !isEmptyContent(memo));

    const handleReorganizeClick = () => {
      onReorganize?.();
      eventTracker(GTMEventName.clickPostAction, {
        page_area: getPrevPageArea(),
        area_name: '문서',
        action_type: '재정리 요청',
        ...getDocumentDetailParam(getValues()),
      });
    };

    // 번역을 켜면 열려 있던 단어 바꾸기 패널을 닫는다 (원문 편집 기능이라 공존 불가)
    useEffect(() => {
      if (wordReplaceDisabled && isWordReplacePanelOpen) {
        onCloseWordReplacePanel?.();
      }
    }, [wordReplaceDisabled, isWordReplacePanelOpen, onCloseWordReplacePanel]);

    // content 비어있는 작성자는 메모 탭으로 강제 전환 (AI 정리는 자동생성 대상)
    useEffect(() => {
      if (contentTabDisabled && viewMode === 'content') {
        setViewMode('memo');
      }
    }, [contentTabDisabled, viewMode]);

    // 정리 생성 시작(스켈레톤) 또는 정리 실패(에러 배너) 시 AI 정리 탭으로 전환해
    // 해당 상태 화면이 보이도록 한다.
    useEffect(() => {
      if (isGenerating || organizeError) setViewMode('content');
    }, [isGenerating, organizeError]);

    const handleOnChange = (
      editorState: EditorState,
      editor: LexicalEditor,
    ) => {
      editorState.read(() => {
        const htmlString = $generateHtmlFromNodes(editor, null);

        // 변경된 내용이 이전 내용과 다를 때만 setValue 호출
        if (htmlString !== prevContent) {
          setValue('content', htmlString);
          setPrevContent(htmlString); // 이전 내용 업데이트
        }
      });
    };

    // 메모 에디터 onChange → form 필드 'memo' 반영
    const handleMemoChange = (
      editorState: EditorState,
      editor: LexicalEditor,
    ) => {
      editorState.read(() => {
        const htmlString = $generateHtmlFromNodes(editor, null);
        if (htmlString !== prevMemo) {
          setValue('memo', htmlString);
          setPrevMemo(htmlString);
        }
      });
    };

    useEffect(() => {
      const scrollTarget = document.getElementsByClassName('modal-content')[0];
      if (!scrollTarget) return;
      const handleButtonPositionUpdate = () => {
        const dropdown = document.getElementsByClassName('dropdown')[0];
        const ContentEditable = document.getElementsByClassName(
          'ContentEditable__root',
        )[0];
        if (dropdown) {
          (ContentEditable as HTMLDivElement).click();
          (ContentEditable as HTMLDivElement).focus();
        }
      };

      scrollTarget.addEventListener('scroll', handleButtonPositionUpdate);
      return () => {
        scrollTarget.removeEventListener('scroll', handleButtonPositionUpdate);
      };
    }, []);

    useEffect(() => {
      if (!content) return;

      // editor에서의 내용 업데이트가 있을 때
      if (
        editorRef.current &&
        content !== prevContent &&
        !isEmptyContent(content)
      ) {
        editorRef.current.update(content);
        setPrevContent(content); // 이전 내용 업데이트
      }
    }, [content]);

    // 토글 전환 시 활성 에디터에 저장된 값 복원
    // (조건부 렌더링으로 에디터가 새로 마운트되므로 값 재주입 필요)
    useEffect(() => {
      if (viewMode === 'content') {
        if (editorRef.current && !isEmptyContent(content)) {
          editorRef.current.update(content);
          setPrevContent(content);
        }
      } else if (viewMode === 'memo') {
        if (memoEditorRef.current && !isEmptyContent(memo)) {
          memoEditorRef.current.update(memo);
          setPrevMemo(memo);
        }
      }
    }, [viewMode]);

    useImperativeHandle(ref, () => ({
      handleAiOnCreate: async (value: string) => {
        // 자동정리 결과는 기존 내용을 무조건 덮어쓴다 (덮어쓰기는 Alert 로 사전 동의받음)
        if (isOwner) {
          setValue('content', await ensureLexicalHtml(value));
        }
      },
      // 작성내역 아래 붙여넣기 — 기존 content 뒤에 이어붙임
      appendToContent: async (text: string) => {
        if (!isOwner) return;
        const html = await ensureLexicalHtml(text);
        const current = getValues('content') || '';
        setViewMode('content');
        setValue(
          'content',
          isEmptyContent(current) ? html : `${current}${html}`,
        );
      },
      // 덮어쓰기 — content 전체 교체
      overwriteContent: async (text: string) => {
        if (!isOwner) return;
        const html = await ensureLexicalHtml(text);
        setViewMode('content');
        setValue('content', html);
      },
    }));

    useEffect(() => {
      aiButtonPropsRef.current = aiButtonProps;
    }, [aiButtonProps]);

    useEffect(() => {
      onRequestAutoSummarizeRef.current = onRequestAutoSummarize;
    }, [onRequestAutoSummarize]);

    const handleMessageEvent = useCallback((event: MessageEvent | Event) => {
      if ('data' in event && typeof event.data === 'string' && event.data) {
        try {
          const parsed = JSON.parse(event?.data);
          switch (parsed.type) {
            case 'OPEN_AI':
              // 앱 공유 진입 시: Chat 을 여는 대신 자동정리(AI 정리)를 실행한다.
              onRequestAutoSummarizeRef.current?.();
              break;
          }
        } catch (e) {
          console.error(e);
        }
      }
    }, []);

    useEffect(() => {
      window.addEventListener('message', handleMessageEvent);
      document.addEventListener('message', (e) => {
        handleMessageEvent(e);
      });
      return () => {
        window.removeEventListener('message', handleMessageEvent);
        document.removeEventListener('message', (e) => handleMessageEvent(e));
      };
    }, [handleMessageEvent]);
    const uploadingData = useAtomValue(uploadingState);

    // 컨텐츠 렌더링 (중복 제거)
    const renderContent = () => {
      if (isEmptyContent(content)) {
        return (
          <EmptyContainer>
            {isOwner
              ? t("내용을 '여기'에 자유롭게 작성해 주세요")
              : t('아직 AI가 정리한 내용이 없어요.')}
          </EmptyContainer>
        );
      }

      return (
        <div style={{ position: 'relative' }}>
          {openAutoAlert && !isWordReplacePanelOpen && (
            <div style={{ position: 'absolute', right: 0 }}>
              <AutoAlert onClick={() => setOpenAutoAlert(false)} />
            </div>
          )}
          <BoardAndRecord
            content={pick('content', content)}
            searchText={''}
            focusedResultId={''}
            hoveredResultId={null}
            scrollTrigger={0}
          />
        </div>
      );
    };

    // 메모 읽기 뷰 렌더링 (renderContent 의 memo 버전)
    const renderMemo = () => {
      if (isEmptyContent(memo)) {
        return (
          <EmptyContainer>
            {isOwner
              ? t('메모를 자유롭게 작성해 주세요')
              : t('작성자가 작성한 메모가 없어요.')}
          </EmptyContainer>
        );
      }

      return (
        <div style={{ position: 'relative' }}>
          <BoardAndRecord
            content={pick('memo', memo)}
            searchText={''}
            focusedResultId={''}
            hoveredResultId={null}
            scrollTrigger={0}
          />
        </div>
      );
    };

    /**
     * AI 정리(content) / 메모(memo) 공용 에디터 영역.
     * owner 편집(editor-shell) / 비owner 읽기(document-editor__content) 구조 동일.
     * 단어 바꾸기 오버레이는 content 전용.
     */
    const renderEditorSection = (field: DocViewMode) => {
      const isMemoField = field === 'memo';
      const value = isMemoField ? memo : content;
      const fieldEditorRef = isMemoField ? memoEditorRef : editorRef;
      const fieldOnChange = isMemoField ? handleMemoChange : handleOnChange;
      const placeholder = isMemoField
        ? t('메모를 자유롭게 작성해 주세요')
        : t("내용을 '여기'에 자유롭게 작성해 주세요");
      const renderReadView = isMemoField ? renderMemo : renderContent;
      const wordReplaceActive =
        !isMemoField && isWordReplacePanelOpen && !wordReplaceDisabled;
      // '재정리 요청' 버튼은 본문(content)·메모(memo) 에디터 툴바 양쪽에 노출.
      // 정리 이력이 없는 문서(메모만 작성)는 AI 정리 탭이 비활성이라 메모 탭에서만
      // 최초 실행이 가능하기 때문(PORTAL-12850). 실행 시 AI 정리 탭으로 자동 전환된다.
      const reorganizeButtonProps = onReorganize
        ? {
            onClick: handleReorganizeClick,
            // 정리 실패 시에는 로딩을 멈춘다 (재시도는 에러 배너가 담당)
            isLoading: isGenerating && !organizeError,
            disabled: !canReorganize,
          }
        : undefined;

      return (
        <>
          <Box
            ref={editorCoverRef}
            onMouseLeave={() => setIsHover(false)}
            onMouseEnter={() => setIsHover(true)}
            className={classNames({
              'document-editor': true,
              hoverCls: isHover,
            })}
            sx={{
              '.document-editor__content': {
                display: 'block',
              },
              '.editor-shell': {
                display: 'none',
              },
              '.toolbar': {
                top: `${topOffset}px`,
              },
              ...(showEditor && {
                '.document-editor__content': {
                  display: 'none',
                },
                '.editor-shell': {
                  display: 'block',
                },
              }),
              ...(wordReplaceActive && {
                height: 0,
                overflow: 'hidden',
                '.document-editor__content': {
                  visibility: 'hidden',
                },
                '.editor-shell': {
                  visibility: 'hidden',
                },
              }),
            }}
          >
            {/* 번역 표시 중에도 에디터는 마운트한 채 display 로만 감춘다.
                언마운트하면 재마운트 시 빈 onChange 가 먼저 돌아 content 가 유실된다
                (AI 정리 생성 중 스켈레톤이 같은 이유로 언마운트하지 않는 것과 동일). */}
            {isOwner && (
              <div style={{ cursor: 'auto', position: 'relative' }}>
                <LexicalBoardEditor
                  ref={fieldEditorRef}
                  isSimple={true}
                  onChange={fieldOnChange}
                  placeholder={placeholder}
                  reorganizeButtonProps={reorganizeButtonProps}
                />
                {!isMemoField && !isEmptyContent(value) && openAutoAlert && (
                  <div style={{ position: 'absolute', right: 12, top: 50 }}>
                    <AutoAlert onClick={() => setOpenAutoAlert(false)} />
                  </div>
                )}
              </div>
            )}

            <Box
              className="document-editor__content"
              sx={{
                padding: '32px 16px 16px 16px',
                minHeight: '398px',
                border: '1px solid transparent',
              }}
            >
              {/* showEditor(=편집기 표시) 중엔 display:none 으로 숨는 읽기뷰 — 매 키 setValue
                  리렌더마다 무거운 BoardAndRecord 를 헛되이 재렌더하므로 미렌더.
                  번역 표시(isTranslationActive) 중엔 !showEditor 라 렌더 유지 → pick() 번역본 출력 보존 */}
              {!showEditor && renderReadView()}
            </Box>
          </Box>

          {/* 단어 바꾸기 패널이 열렸을 때: 오버레이 (content 전용) */}
          {wordReplaceActive && (
            <Box
              onClick={() => {
                dialog.show({
                  message: (
                    <>
                      {t(
                        'WordReplace.단어 바꾸기 이용중에는 회의록을 수정할 수 없습니다.',
                      )}
                      <br />
                      {t('WordReplace.패널을 닫으시겠어요?')}
                    </>
                  ),
                  confirmButtonText: t('WordReplace.패널 닫기'),
                  cancelButtonText: t('취소'),
                  onConfirm: () => {
                    onCloseWordReplacePanel?.();
                  },
                });
              }}
              sx={{
                backgroundColor: 'var(--dk-bg, white)',
                padding: '32px 16px 16px 16px',
                minHeight: '398px',
                cursor: 'pointer',
              }}
            >
              {isEmptyContent(content) ? (
                <EmptyContainer>
                  {isOwner
                    ? t("내용을 '여기'에 자유롭게 작성해 주세요")
                    : t('아직 AI가 정리한 내용이 없어요.')}
                </EmptyContainer>
              ) : (
                <BoardAndRecord
                  content={content}
                  searchText={searchText}
                  focusedResultId={focusedResultId}
                  hoveredResultId={hoveredResultId}
                  scrollTrigger={scrollTrigger}
                />
              )}
            </Box>
          )}
        </>
      );
    };

    return (
      <Box
        sx={{
          margin: '0 24px',
          padding: isMobile ? '24px 0 0' : '24px 0',
          borderTop: '1px solid var(--dk-line, #E7E9EC)',
        }}
      >
        {/* AI 요약(commonSummary) — 본문 탭(ContentHeader) 위에 배치 (디자인), /summarize 완료 시 자동 갱신 */}
        <AiSummary docId={docId} isOwner={isOwner} />

        <ContentHeader
          aiButtonProps={aiButtonProps}
          wordReplaceButtonProps={
            wordReplaceButtonProps && {
              ...wordReplaceButtonProps,
              disabled: wordReplaceDisabled,
            }
          }
          isOwner={isOwner}
          viewMode={viewMode}
          onChangeViewMode={setViewMode}
          contentDisabled={contentTabDisabled}
          showMemoTab={showMemoTab}
          // 정리 실패 시에는 에러 배너가 뜨므로 탭 스피너를 함께 멈춘다
          isOrganizing={isGenerating && !organizeError}
        />

        <Box
          style={{
            cursor: 'auto',
            padding: '24px 0',
            margin: `0 ${isMobile ? `${8 - 20}px` : 0}`,
            position: 'relative',
          }}
        >
          {/* AI 정리 실패 — 본문 영역에 에러 배너(재시도) 표시 (Figma 26255:504942).
              AI 요약 블록과 분리된 organize 전용 에러. 스켈레톤/에디터 대신 노출한다. */}
          {organizeError && viewMode === 'content' && (
            <Box sx={{ padding: '32px 16px 16px 16px', minHeight: '398px' }}>
              <OrganizeError onRetry={onRetryOrganize} />
            </Box>
          )}
          {/* AI 정리 생성 중 — 본문 영역에 스켈레톤 표시.
              단, 에디터를 언마운트하면 재마운트 시 빈 onChange 로 content 가 유실되므로
              에디터는 마운트한 채(display:none) 두고 스켈레톤만 위에 보여준다.
              (에러/완료로 스켈레톤이 걷혀도 기존 content 가 그대로 남는다) */}
          {isGenerating && !organizeError && viewMode === 'content' && (
            // 메모 탭으로 전환하면 메모는 계속 작성 가능
            <Box sx={{ padding: '32px 16px 16px 16px' }}>
              <OrganizingSkeleton />
            </Box>
          )}
          <Box
            sx={{
              display:
                (isGenerating || organizeError) && viewMode === 'content'
                  ? 'none'
                  : 'block',
            }}
          >
            {/* AI 정리(content) 모드 */}
            {viewMode === 'content' && renderEditorSection('content')}

            {/* 메모(memo) 모드 — content 와 동일 구조. 작성자는 편집,
                비작성자(공유 문서)는 renderEditorSection 내부에서 읽기 전용으로 렌더 */}
            {viewMode === 'memo' && showMemoTab && renderEditorSection('memo')}
          </Box>
        </Box>
        <Container>
          <div style={{ flexGrow: 1 }}>
            <DetailContent
              iconName={'voice'}
              title={t('음성 파일')}
              content={
                <>
                  <UploadingFile uploadingData={uploadingData.record} />
                  {isOwner && <UploadFailedNotice docId={docId} />}
                  <ModifyLayout
                    isOwner={isOwner}
                    modifyComponent={<RecordUpload />}
                    mobileMode={isMobilePath}
                  >
                    {records.length > 0 ? (
                      <RecordContainer mobileMode={isMobilePath}>
                        {records.map((res) => (
                          <FileItem key={res.uuid} fileName={res.name} />
                        ))}
                      </RecordContainer>
                    ) : (
                      <StyledPlaceHolder>
                        {isOwner
                          ? t(
                              '파일을 업로드 하거나 녹음을 시작하면 참석자 모두 동의한 것으로 간주됩니다. 내용에 개인정보를 포함하지 않도록 주의해 주세요.',
                            )
                          : ''}
                      </StyledPlaceHolder>
                    )}
                  </ModifyLayout>
                </>
              }
              rightContent={
                isOwner &&
                !isMobilePath && (
                  <div style={{ position: 'relative' }} ref={contentRef}>
                    <div style={{ position: 'absolute', right: 0 }}>
                      <VoiceRecord2
                        docId={docId}
                        onFirstStart={() => {
                          eventTracker(GTMEventName.clickPostAction, {
                            page_area: getPrevPageArea(),
                            area_name: '문서',
                            action_type: '녹음',
                            ...getDocumentDetailParam(getValues()),
                          });
                        }}
                        style={{
                          boxShadow:
                            '0px 0px 2px 0px rgba(0, 0, 0, 0.12), 0px 0px 4px 0px rgba(0, 0, 0, 0.08) inset',
                        }}
                      />
                    </div>
                  </div>
                )
              }
            />
          </div>
          {isOwner && !isMobilePath && !entry?.isIntersecting && (
            <div style={{ position: 'fixed', bottom: 80, right: 15 }}>
              <div style={{ position: 'absolute', top: 0, right: 0 }}>
                <VoiceRecord2 docId={docId} />
              </div>
            </div>
          )}
        </Container>
      </Box>
    );
  },
);

Contents.displayName = 'TalkFlowContent';

export default Contents;

const RecordContainer = styled.div<{ mobileMode: boolean }>`
  display: flex;
  gap: 8px;
  flex-grow: 1;
  flex-wrap: wrap;
  margin-top: ${({ mobileMode }) => (mobileMode ? '-12px' : '')};
`;

const Container = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-top: 1px solid var(--dk-line, #e7e9ec);
  border-bottom: 1px solid var(--dk-line, #e7e9ec);
  display: flex;
  align-items: center;
`;

const EmptyContainer = styled.div`
  color: var(--dk-text-dim, #b7bbc2);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  min-height: 140px;
  padding-top: 20px;
`;

const StyledPlaceHolder = styled.div`
  color: var(--dk-text-dim, #b7bbc2);
  text-align: left;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px; /* 200% */
`;
